import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, Outlet, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollToTopButton from './ScrollToTopButton';
import useIsPhoneLayout from './util/useIsPhoneLayout';
import LanguageSelector from './components/LanguageSelector';
import { applyLanguage, normalizeLanguage } from './i18n';
import { setLiveAuthUser, PERMISSIONS_CHANGED_EVENT, canAccess } from './util/userRoles';

function pathnameToCurrentPage(pathname, search = '') {
  const p = (pathname || '').replace(/\/$/, '') || '/';
  if (p === '/' || p === '') return 'dashboard';
  if (p === '/patrol-admin' || p === '/admin') return 'patrol-admin';
  if (p === '/patrol-checklists' || p === '/patrol-checklists/') return 'patrol-checklists';
  if (p === '/items/categories' || p.startsWith('/items/categories')) return 'categories';
  if (p === '/items/suppliers' || p.startsWith('/items/suppliers')) return 'suppliers';
  if (p === '/items' || p === '/items/') return 'items';
  if (p === '/proposal') return 'proposal';
  if (p === '/proposals' || p === '/proposals/') return 'proposals';
  if (p === '/users-admin') return 'users-admin';
  if (p === '/roles-admin' || p === '/roles-admin/') return 'roles-admin';
  if (p === '/warehouse') {
    const tab = new URLSearchParams(search).get('tab') || 'Stock';
    const map = {
      Stock: 'warehouse-stock',
      Receipts: 'warehouse-receipts',
      Issues: 'warehouse-issues',
      Transfers: 'warehouse-transfers',
      Purchases: 'warehouse-purchases',
      Ledger: 'warehouse-ledger',
      Warehouses: 'warehouse-locations'
    };
    return map[tab] || 'warehouse-stock';
  }
  if (p === '/clients') return 'clients';
  if (p === '/subscriptions') return 'subscriptions';
  if (p === '/customers' || p === '/customers/') return 'customers';
  if (p === '/contracts') return 'contracts';
  if (p === '/scheduler') return 'scheduler';
  if (p === '/projects' || p === '/projects/' || p.startsWith('/projects/')) return 'projects';
  if (p === '/budget' || p === '/budget/') return 'budget';
  if (p === '/accounting' || p === '/accounting/') return 'accounting-hub';
  if (p === '/accounting/proformas' || p === '/accounting/proformas/') return 'accounting-proformas';
  if (p.startsWith('/accounting/sales-invoices')) return 'accounting-sales';
  if (p.startsWith('/accounting/purchases')) return 'accounting-purchases';
  if (p.startsWith('/accounting/accounts')) return 'accounting-accounts';
  if (p.startsWith('/accounting/journals')) return 'accounting-journals';
  if (p.startsWith('/accounting/vat')) return 'accounting-vat';
  if (p.startsWith('/accounting/payroll')) return 'accounting-payroll';
  if (p.startsWith('/accounting/reports')) return 'accounting-reports';
  if (p.startsWith('/accounting/filings')) return 'accounting-filings';
  if (p.startsWith('/accounting/settings')) return 'accounting-settings';
  if (p.startsWith('/accounting/qb-import')) return 'accounting-qb';
  if (p.startsWith('/accounting')) return 'accounting-hub';
  if (p === '/employees' || p === '/employees/' || p.startsWith('/employees/')) return 'employees';
  if (p.startsWith('/organization/standards')) return 'organization-standards';
  if (p.startsWith('/organization/job-descriptions')) return 'organization';
  if (p === '/organization' || p === '/organization/' || p.startsWith('/organization')) {
    if (String(search || '').includes('tab=settings')) return 'organization-settings';
    return 'organization';
  }
  if (p === '/phase-templates') return 'phase-templates';
  if (p === '/settings' || p === '/settings/' || p.startsWith('/settings/')) {
    if (p.includes('/installers')) return 'settings-installers';
    if (p.includes('/asset-types')) return 'settings-asset-types';
    if (p.includes('/integrations')) return 'settings-integrations';
    if (p.includes('/banks')) return 'settings-banks';
    if (p.includes('/templates')) return 'settings-templates';
    if (p.includes('/backup')) return 'settings-backup';
    return 'settings-company';
  }
  if (p === '/neural-train') return 'neural-train';
  if (p === '/metrics' || p === '/metrics/') {
    const section = new URLSearchParams(search).get('section') || 'sales-leads';
    const map = {
      'sales-leads': 'metrics-sales-leads',
      marketing: 'metrics-marketing',
      'web-shop': 'metrics-web-shop',
      'partner-orders': 'metrics-partner-orders'
    };
    return map[section] || 'metrics-sales-leads';
  }
  if (p === '/sales/leads' || p === '/sales/leads/' || p.startsWith('/sales/leads/')) return 'sales-leads';
  if (p === '/sales/lead-sources') return 'sales-lead-sources';
  if (p === '/sales/reminders') return 'sales-reminders';
  if (p === '/sales/dashboard') return 'sales-dashboard';
  if (p === '/sales/kpi' || p === '/sales/kpi/') return 'sales-kpi';
  if (p === '/sales/marketing') return 'analytics-marketing';
  if (p === '/sales/webshop' || p === '/sales/webshop/') return 'sales-webshop';
  if (p === '/sales/developers' || p === '/sales/developers/') return 'sales-developers';
  if (p.startsWith('/sales/developers/')) return 'sales-developers';
  if (p === '/email-campaigns' || p === '/email-campaigns/') return 'email-campaigns';
  if (p === '/marketing/presentations' || p === '/marketing/presentations/') return 'marketing-presentations';
  if (p === '/quality' || p === '/quality/') return 'quality';
  if (p === '/payment-approval') return 'payment-approval';
  if (p === '/analytics/webshop' || p === '/analytics/webshop/') return 'metrics-web-shop';
  if (p === '/analytics' || p === '/analytics/' || p.startsWith('/analytics/')) {
    const tab = new URLSearchParams(search).get('tab') || 'Proposals';
    const map = {
      Proposals: 'analytics-proposals',
      Marketing: 'analytics-marketing',
      'Partner orders': 'analytics-partner-orders',
      Purchases: 'analytics-purchases',
      'Price dynamics': 'analytics-price-dynamics',
      Agents: 'analytics-agents'
    };
    return map[tab] || 'analytics-proposals';
  }
  if (p === '/fleet' || p === '/fleet/') return 'fleet';
  if (p === '/orders' || p === '/orders/') return 'orders';
  if (p === '/partner/shop' || p.startsWith('/partner/shop')) return 'partner-shop';
  if (p === '/partner/orders' || p.startsWith('/partner/orders')) return 'partner-orders';
  if (p === '/agent/commissions' || p.startsWith('/agent/commissions')) return 'agent-commissions';
  if (p === '/partner-orders' || p === '/partner-orders/') return 'partner-orders-admin';
  if (p === '/agent-orders' || p === '/agent-orders/') return 'agent-orders';
  if (p === '/super-admin/tenants' || p === '/super-admin/tenants/') return 'super-admin-tenants';
  if (p === '/change-password') return null;
  return null;
}

function pathnameToPageTitle(pathname, search = '', tNav) {
  const t = typeof tNav === 'function' ? tNav : ((k, d) => d || k);
  const p = (pathname || '').replace(/\/$/, '') || '/';
  if (p === '/' || p === '') return t('dashboard', 'Dashboard');
  if (p === '/patrol-admin' || p === '/admin') return t('patrolAdmin', 'Patrol Admin');
  if (p === '/patrol-checklists' || p === '/patrol-checklists/') return t('patrolChecklists', 'Patrol checklists');
  if (p === '/items/categories' || p.startsWith('/items/categories')) return t('categories', 'Categories');
  if (p === '/items/suppliers' || p.startsWith('/items/suppliers')) return t('suppliers', 'Suppliers');
  if (p === '/items' || p === '/items/') return t('items', 'Items');
  if (p === '/proposal') return t('proposalBuilder', 'Proposal Builder');
  if (p === '/proposals' || p === '/proposals/') return t('proposals', 'Proposals');
  if (p === '/users-admin') return t('users', 'Users');
  if (p === '/roles-admin' || p === '/roles-admin/') return t('roles', 'Roles');
  if (p === '/warehouse') {
    const tab = new URLSearchParams(search).get('tab') || 'Stock';
    const map = {
      Stock: t('stock', 'Stock'),
      Receipts: t('receipts', 'Receipts'),
      Issues: t('issues', 'Issues'),
      Transfers: t('transfers', 'Transfers'),
      Purchases: t('purchases', 'Purchases'),
      Ledger: t('ledger', 'Ledger'),
      Warehouses: t('warehouses', 'Warehouses')
    };
    return map[tab] || t('groupWarehouse', 'Warehouse');
  }
  if (p === '/clients') return t('clients', 'Clients');
  if (p === '/subscriptions') return t('subscriptions', 'Subscriptions');
  if (p === '/customers' || p === '/customers/') return 'Customer Portal';
  if (p === '/contracts') return t('contracts', 'Contracts');
  if (p === '/scheduler') return t('scheduler', 'Scheduler');
  if (p === '/projects' || p === '/projects/') return t('projects', 'Projects');
  if (p === '/projects/new') return 'New project';
  if (p.startsWith('/projects/')) return 'Project';
  if (p === '/budget' || p === '/budget/') return t('budget', 'Budget');
  if (p === '/accounting' || p === '/accounting/') return t('sectionAccounting', 'Accounting');
  if (p.startsWith('/accounting/proformas')) return t('proformas', 'Proformas');
  if (p.startsWith('/accounting/sales-invoices')) return t('salesInvoices', 'Sales invoices');
  if (p.startsWith('/accounting/purchases')) return t('purchases', 'Purchases');
  if (p.startsWith('/accounting/accounts')) return t('chartOfAccounts', 'Chart of accounts');
  if (p.startsWith('/accounting/journals')) return t('journals', 'Journals');
  if (p.startsWith('/accounting/vat')) return t('vat', 'VAT');
  if (p.startsWith('/accounting/payroll')) return t('payroll', 'Payroll');
  if (p.startsWith('/accounting/reports')) return t('reports', 'Reports');
  if (p.startsWith('/accounting/filings')) return t('filings', 'Filings');
  if (p.startsWith('/accounting/settings')) return t('settings', 'Accounting settings');
  if (p.startsWith('/accounting/qb-import')) return t('qbImport', 'QuickBooks import');
  if (p.startsWith('/accounting')) return t('sectionAccounting', 'Accounting');
  if (p === '/employees' || p === '/employees/') return t('employees', 'Employees');
  if (p.startsWith('/employees/')) return 'Employee';
  if (p.startsWith('/organization/standards')) return t('orgStandards', 'Company standards');
  if (p.startsWith('/organization/job-descriptions/')) return t('orgJobDescription', 'Job description');
  if (p === '/organization' || p === '/organization/' || p.startsWith('/organization')) {
    if (String(search || '').includes('tab=settings')) return t('orgSettings', 'Settings');
    return t('orgBoard', 'Org chart');
  }
  if (p === '/phase-templates') return 'Phase templates';
  if (p === '/settings' || p === '/settings/' || p.startsWith('/settings/')) {
    if (p.includes('/installers')) return t('installers', 'Installers');
    if (p.includes('/asset-types')) return t('assetTypes', 'Asset types');
    if (p.includes('/integrations')) return t('integrations', 'Integrations');
    if (p.includes('/banks')) return t('banks', 'Banks');
    if (p.includes('/templates')) return t('templates', 'Templates');
    if (p.includes('/backup')) return t('backup', 'Backup');
    return t('companySetting', 'Company Setting');
  }
  if (p === '/neural-train') return t('neuralClassifier', 'Neural classifier training');
  if (p === '/metrics' || p === '/metrics/') {
    const section = new URLSearchParams(search).get('section') || 'sales-leads';
    const map = {
      'sales-leads': t('salesAndLeads', 'Sales & Leads'),
      marketing: t('marketing', 'Marketing metrics'),
      'web-shop': t('webShop', 'Web Shop metrics'),
      'partner-orders': t('partnerOrders', 'Partner orders metrics')
    };
    return map[section] || t('sectionMetrics', 'Metrics');
  }
  if (p === '/sales/leads' || p === '/sales/leads/') return t('leads', 'Leads');
  if (p.startsWith('/sales/leads/')) return 'Lead';
  if (p === '/sales/lead-sources') return t('leadSources', 'Lead sources');
  if (p === '/sales/reminders') return t('reminders', 'Reminders');
  if (p === '/sales/dashboard') return t('dashboard', 'Dashboard');
  if (p === '/sales/kpi' || p === '/sales/kpi/') return t('kpi', 'KPI');
  if (p === '/sales/marketing') return t('marketing', 'Marketing');
  if (p === '/sales/webshop' || p === '/sales/webshop/') return t('webShop', 'Web Shop');
  if (p === '/sales/developers' || p === '/sales/developers/') return t('developers', 'Developers');
  if (p.startsWith('/sales/developers/')) return 'Developer';
  if (p === '/email-campaigns' || p === '/email-campaigns/') return t('emailCampaigns', 'Email campaigns');
  if (p === '/marketing/presentations' || p === '/marketing/presentations/') return t('productPresentations', 'Product presentations');
  if (p === '/quality' || p === '/quality/') return t('quality', 'Customer Quality');
  if (p === '/payment-approval') return t('paymentApproval', 'Payment approval');
  if (p === '/analytics/webshop' || p === '/analytics/webshop/') return t('webShop', 'Web Shop metrics');
  if (p === '/analytics' || p === '/analytics/' || p.startsWith('/analytics/')) {
    const tab = new URLSearchParams(search).get('tab') || 'Proposals';
    return tab || t('sectionAnalytics', 'Analytics');
  }
  if (p === '/fleet' || p === '/fleet/') return t('fleet', 'Fleet');
  if (p === '/orders' || p === '/orders/') return t('orders', 'Orders');
  if (p === '/partner/shop' || p.startsWith('/partner/shop')) return t('shop', 'Shop');
  if (p === '/partner/orders' || p.startsWith('/partner/orders')) return t('orders', 'Orders');
  if (p === '/agent/commissions' || p.startsWith('/agent/commissions')) return t('myCommissions', 'My commissions');
  if (p === '/partner-orders' || p === '/partner-orders/') return t('partnerOrders', 'Partner orders');
  if (p === '/agent-orders' || p === '/agent-orders/') return 'Agent orders';
  if (p === '/super-admin/tenants' || p === '/super-admin/tenants/') return t('tenants', 'Companies');
  if (p === '/change-password') return t('changePassword', 'Change password');
  if (p === '/patrol') return t('openForm', 'Open Form');
  return 'SFT Admin Panel';
}

export default function AppLayout() {
  const { t } = useTranslation('common');
  const { t: tNav, i18n } = useTranslation('nav');
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [tenantSwitching, setTenantSwitching] = useState(false);
  const currentPage = pathnameToCurrentPage(location.pathname, location.search);
  const pageTitle = pathnameToPageTitle(location.pathname, location.search, tNav);
  const pathForLayout = (location.pathname || '').replace(/\/$/, '') || '/';
  const isClientForTopbar = user && (user.role || '').toLowerCase() === 'client';
  const hideTopbarForHooks = currentPage === 'customers' || (isClientForTopbar && pathForLayout === '/patrol');

  const isMobileNavViewport = useIsPhoneLayout(768);
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);

  const applyMe = useCallback(async (j, { setChecked } = {}) => {
    if (j && j.ok && j.user) {
      setUser(j.user);
      setLiveAuthUser(j.user);
      await applyLanguage(j.user.preferredLanguage || 'en');
    } else {
      setLiveAuthUser(null);
    }
    if (setChecked) setAuthChecked(true);
  }, []);

  const refreshMe = useCallback((opts = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    fetch('/auth/me', { credentials: 'include', cache: 'no-store', signal: controller.signal })
      .then((r) => {
        const ct = String(r.headers.get('content-type') || '').toLowerCase();
        if (!r.ok || !ct.includes('application/json')) return null;
        return r.json().catch(() => null);
      })
      .then((j) => applyMe(j, opts))
      .catch(() => {
        if (opts.setChecked) setAuthChecked(true);
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [applyMe]);

  // Initial auth
  useEffect(() => refreshMe({ setChecked: true }), [refreshMe]);

  // Re-fetch permissions when route changes, tab gains focus, or Roles admin saves
  useEffect(() => {
    const onFocus = () => refreshMe();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshMe();
    };
    const onPerms = () => refreshMe();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, onPerms);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(PERMISSIONS_CHANGED_EVENT, onPerms);
    };
  }, [refreshMe]);

  useEffect(() => {
    if (!authChecked) return undefined;
    return refreshMe();
  }, [location.pathname, authChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentPage === 'customers' || typeof window === 'undefined' || !window.SFTNavMenu) return;
    window.SFTNavMenu.init({
      currentPage,
      user,
      lang: normalizeLanguage(i18n.language || user?.preferredLanguage || 'en'),
      onLogout: async () => {
        try {
          await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
        } catch (_) {}
        try {
          localStorage.removeItem('patrolToken');
          localStorage.removeItem('patrolRole');
          localStorage.removeItem('token');
        } catch (_) {}
        window.location.replace('/login?loggedout=1&next=' + encodeURIComponent('/'));
      }
    });
  }, []);

  useEffect(() => {
    if (currentPage === 'customers' || typeof window === 'undefined' || !window.SFTNavMenu) return;
    window.SFTNavMenu.update({
      currentPage,
      user,
      lang: normalizeLanguage(i18n.language || user?.preferredLanguage || 'en')
    });
  }, [currentPage, user, i18n.language]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.documentElement.classList.toggle('sft-phone-layout', isMobileNavViewport);
    document.body.classList.toggle('sft-phone-layout', isMobileNavViewport);
    return () => {
      document.documentElement.classList.remove('sft-phone-layout');
      document.body.classList.remove('sft-phone-layout');
    };
  }, [isMobileNavViewport]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const useSidebar = !hideTopbarForHooks;
    document.documentElement.classList.toggle('sft-layout-sidebar', useSidebar);
    document.body.classList.toggle('sft-layout-sidebar', useSidebar);
    if (!useSidebar) {
      document.body.classList.remove('sft-sidebar-open');
      window.SFTNavMenu?.closeMenu?.();
    }
    return () => {
      document.documentElement.classList.remove('sft-layout-sidebar');
      document.body.classList.remove('sft-layout-sidebar', 'sft-sidebar-open');
    };
  }, [hideTopbarForHooks]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.SFTNavMenu?.closeMenu?.();
  }, [location.pathname]);

  useEffect(() => {
    if (!authChecked || !user || hideTopbarForHooks || !isMobileNavViewport) return undefined;
    const el = document.getElementById('sft-nav-menu-container');
    if (!el) return undefined;
    const sync = () => setMobileNavMenuOpen(
      el.classList.contains('is-rail-open') || el.classList.contains('is-open')
    );
    const ob = new MutationObserver(sync);
    ob.observe(el, { attributes: true, attributeFilter: ['class'] });
    sync();
    return () => ob.disconnect();
  }, [authChecked, user, hideTopbarForHooks, isMobileNavViewport, currentPage]);

  useEffect(() => {
    if (!authChecked || !user || hideTopbarForHooks || !isMobileNavViewport) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') window.SFTNavMenu?.closeMenu?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [authChecked, user, hideTopbarForHooks, isMobileNavViewport]);

  if (!authChecked) {
    return (
      <div className="sft-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="sft-hint">{t('checkingAuth')}</div>
      </div>
    );
  }

  if (!user) {
    const next = (location.pathname || '/').replace(/\/$/, '') || '/';
    window.location.replace('/login?next=' + encodeURIComponent(next === '/' ? '/' : next));
    return (
      <div className="sft-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
        <div className="sft-hint">{t('redirectingLogin')}</div>
      </div>
    );
  }

  const isClient = user && (user.role || '').toLowerCase() === 'client';
  const isCustomersPage = currentPage === 'customers';
  const path = pathForLayout;
  const isPatrolForm = path === '/patrol';
  if (isClient && !isCustomersPage && !isPatrolForm) {
    return <Navigate to="/customers" replace />;
  }

  const isMarketing = user && (user.role || '').toLowerCase() === 'marketing';
  const isSalesPath = path === '/sales' || path.startsWith('/sales/');
  const isChangePassword = path === '/change-password';
  // Marketing soft-gate kept for legacy; permission matrix is authoritative when present.
  if (isMarketing && user?.permissions && currentPage && currentPage !== 'dashboard' && !isChangePassword) {
    if (!canAccess(user, currentPage, 'view')
      && !(currentPage === 'quality' && Array.isArray(user.qualityPermissions) && user.qualityPermissions.length)) {
      return <Navigate to="/sales/leads" replace />;
    }
  } else if (isMarketing) {
    const isMarketingAllowedPath = isSalesPath
      || isChangePassword
      || path === '/analytics' || path.startsWith('/analytics/')
      || path === '/email-campaigns' || path.startsWith('/email-campaigns/')
      || path === '/marketing/presentations' || path.startsWith('/marketing/presentations/')
      || path === '/quality' || path.startsWith('/quality/');
    if (!isMarketingAllowedPath) {
      return <Navigate to="/sales/leads" replace />;
    }
  }

  const userRole = (user?.role || '').toLowerCase();
  const userRoles = Array.isArray(user?.roles) ? user.roles.map((r) => String(r).toLowerCase()) : [];
  const hasPartnerRole = userRole === 'partner' || userRoles.includes('partner');
  const hasAgentRole = userRole === 'agent' || userRoles.includes('agent');
  // Partner/agent portal-only when primary role is partner or agent (staff roles use the matrix).
  const partnerOrAgentOnly = userRole === 'partner' || userRole === 'agent';
  const isPartnerShopPath = path === '/partner/shop' || path.startsWith('/partner/shop')
    || path === '/partner/orders' || path.startsWith('/partner/orders');
  const isAgentCommissionsPath = path === '/agent/commissions' || path.startsWith('/agent/commissions');
  const isLegacyOrdersPath = path === '/orders' || path.startsWith('/orders/');
  if (partnerOrAgentOnly && !isChangePassword) {
    const sp = new URLSearchParams(location.search || '');
    const isVivaReturn = !!(sp.get('t') || sp.get('s') || sp.get('paid') || sp.get('orderId'));
    if (isLegacyOrdersPath || (isVivaReturn && !isPartnerShopPath && !isAgentCommissionsPath)) {
      if (hasPartnerRole) {
        return <Navigate to={`/partner/orders${location.search || ''}`} replace />;
      }
      if (hasAgentRole) {
        return <Navigate to="/agent/commissions" replace />;
      }
    }
    if (!isPartnerShopPath && !isAgentCommissionsPath) {
      if (hasPartnerRole) return <Navigate to="/partner/shop" replace />;
      if (hasAgentRole) return <Navigate to="/agent/commissions" replace />;
    }
    if (isPartnerShopPath && !hasPartnerRole) {
      return <Navigate to="/agent/commissions" replace />;
    }
    if (isAgentCommissionsPath && !hasAgentRole) {
      return <Navigate to="/partner/shop" replace />;
    }
  }

  if (currentPage === 'neural-train' && (!user || (user.username || '').toString().toLowerCase() !== 'pevstifeev')) {
    return <Navigate to="/" replace />;
  }

  // Permission matrix: block pages without view (except always-allowed shells).
  if (
    user
    && user.permissions
    && currentPage
    && !isChangePassword
    && !isCustomersPage
    && userRole !== 'client'
    && !partnerOrAgentOnly
    && currentPage !== 'dashboard'
    && currentPage !== 'phase-templates'
  ) {
    const ok = canAccess(user, currentPage, 'view')
      || (currentPage === 'quality' && Array.isArray(user.qualityPermissions) && user.qualityPermissions.length > 0)
      || ((userRole === 'admin' || userRole === 'super_admin') && (currentPage === 'users-admin' || currentPage === 'roles-admin'));
    if (!ok) {
      return <Navigate to="/" replace />;
    }
  }

  const hideTopbar = hideTopbarForHooks;

  function handleMobileNavFabClick(e) {
    e.preventDefault();
    e.stopPropagation();
    window.SFTNavMenu?.toggleMenu?.();
  }

  async function switchCompany(tenantId) {
    if (!tenantId || tenantSwitching || tenantId === user?.tenantId) return;
    setTenantSwitching(true);
    try {
      const res = await fetch('/auth/switch-tenant', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ tenantId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Switch failed');
      window.location.assign('/');
    } catch (err) {
      window.alert(err.message || 'Could not switch company');
      setTenantSwitching(false);
    }
  }

  return (
    <>
      {!hideTopbar && (
        <>
          <aside id="sft-nav-menu-container" className="sft-sidebar" aria-label="Main navigation" />
          <div className={`sft-topbar-wrap${isMobileNavViewport ? ' sft-topbar-wrap--mobile-fab' : ''}`}>
            <header className="sft-topbar">
              <div className="sft-brand">
                <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                  <img src={(process.env.PUBLIC_URL || '') + '/assets/sft-logo.png'} alt="SFT logo" />
                </a>
              </div>
              <div className="sft-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t('adminPanel')} — {pageTitle}{user ? ` — ${user.username} (${user.role || 'user'})` : ''}
                  {user?.tenantName ? ` — ${user.tenantName}` : ''}
                </span>
                {Array.isArray(user?.tenants) && user.tenants.length > 1 ? (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                    <span className="sft-hint" style={{ fontSize: 11 }}>Company</span>
                    <select
                      className="sft-input"
                      value={user.tenantId || ''}
                      disabled={tenantSwitching}
                      onChange={(e) => switchCompany(e.target.value)}
                      style={{ minWidth: 160, maxWidth: 260, height: 32, padding: '4px 8px', fontSize: 12 }}
                      aria-label="Switch company"
                    >
                      {user.tenants.map((tn) => (
                        <option key={tn.id} value={tn.id}>
                          {tn.name || tn.slug || tn.id}{tn.isHome ? ' (home)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <LanguageSelector
                  compact
                  user={user}
                  onUserLanguageChange={(lang) => setUser((u) => (u ? { ...u, preferredLanguage: lang } : u))}
                  style={{ marginLeft: 'auto', flexShrink: 0 }}
                />
              </div>
            </header>
          </div>
        </>
      )}
      <div className={`sft-wrap${isMobileNavViewport ? ' sft-wrap--phone' : ''}${!hideTopbar ? ' sft-wrap--with-sidebar' : ''}`}>
        <Outlet />
      </div>
      {!hideTopbar && isMobileNavViewport && (
        <button
          type="button"
          id="sft-mobile-nav-fab"
          className="sft-mobile-nav-fab"
          onClick={handleMobileNavFabClick}
          aria-label={t('menu')}
          aria-expanded={mobileNavMenuOpen}
          aria-controls="sft-nav-menu-container"
        >
          <span className="sft-mobile-nav-fab-line" aria-hidden="true" />
          <span className="sft-mobile-nav-fab-line" aria-hidden="true" />
          <span className="sft-mobile-nav-fab-line" aria-hidden="true" />
        </button>
      )}
      <ScrollToTopButton />
    </>
  );
}
